import { useState } from 'react'
import {
  CheckCircle,
  XCircle,
  Sparkles,
  Key,
  Loader2,
  ChevronDown,
  Eye,
  EyeOff,
} from 'lucide-react'
import type { AISettingsProps, AIMode, ProviderId } from './types'

export function AISettings({
  config,
  providers,
  onModeChange,
  onProviderChange,
  onApiKeyChange,
  onSaveApiKey,
  onModelChange,
  validationState,
}: AISettingsProps) {
  const [showApiKey, setShowApiKey] = useState(false)
  const [localApiKey, setLocalApiKey] = useState(config.apiKey || '')

  const selectedProvider = providers.find(p => p.id === config.provider)
  const isValidated = config.validationStatus === 'valid'
  const isValidating = validationState.status === 'validating'

  const handleApiKeySubmit = () => {
    onApiKeyChange(localApiKey)
    onSaveApiKey()
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100">
          AI Configuration
        </h1>
        <p className="mt-1 text-stone-600 dark:text-stone-400">
          Choose how you want to power your inbox summaries
        </p>
      </div>

      {/* Mode Selection - side by side on larger screens */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Built-in AI Option */}
        <label
          className={`
            relative flex cursor-pointer rounded-xl border-2 p-5 transition-all
            ${config.mode === 'built-in'
              ? 'border-teal-500 bg-teal-50 dark:border-teal-400 dark:bg-teal-950/30'
              : 'border-stone-200 bg-white hover:border-stone-300 dark:border-stone-700 dark:bg-stone-800 dark:hover:border-stone-600'
            }
          `}
        >
          <input
            type="radio"
            name="ai-mode"
            value="built-in"
            checked={config.mode === 'built-in'}
            onChange={() => onModeChange('built-in')}
            className="sr-only"
          />
          <div className="flex flex-col items-center text-center gap-3">
            <div className={`
              flex h-12 w-12 shrink-0 items-center justify-center rounded-xl
              ${config.mode === 'built-in'
                ? 'bg-teal-500 text-white'
                : 'bg-stone-100 text-stone-500 dark:bg-stone-700 dark:text-stone-400'
              }
            `}>
              <Sparkles className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center justify-center gap-2">
                <span className="font-medium text-stone-900 dark:text-stone-100">
                  Use Built-in AI
                </span>
              </div>
              <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
                No configuration needed. We handle everything for you.
              </p>
              {config.mode === 'built-in' && (
                <span className="mt-2 inline-block rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-700 dark:bg-teal-900 dark:text-teal-300">
                  Active
                </span>
              )}
            </div>
          </div>
        </label>

        {/* Bring Your Own Key Option */}
        <label
          className={`
            relative flex cursor-pointer rounded-xl border-2 p-5 transition-all
            ${config.mode === 'own-key'
              ? 'border-teal-500 bg-teal-50 dark:border-teal-400 dark:bg-teal-950/30'
              : 'border-stone-200 bg-white hover:border-stone-300 dark:border-stone-700 dark:bg-stone-800 dark:hover:border-stone-600'
            }
          `}
        >
          <input
            type="radio"
            name="ai-mode"
            value="own-key"
            checked={config.mode === 'own-key'}
            onChange={() => onModeChange('own-key')}
            className="sr-only"
          />
          <div className="flex flex-col items-center text-center gap-3">
            <div className={`
              flex h-12 w-12 shrink-0 items-center justify-center rounded-xl
              ${config.mode === 'own-key'
                ? 'bg-teal-500 text-white'
                : 'bg-stone-100 text-stone-500 dark:bg-stone-700 dark:text-stone-400'
              }
            `}>
              <Key className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center justify-center gap-2">
                <span className="font-medium text-stone-900 dark:text-stone-100">
                  Bring Your Own API Key
                </span>
              </div>
              <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
                Use your own OpenAI or Anthropic API key for full control.
              </p>
              {config.mode === 'own-key' && isValidated && (
                <span className="mt-2 inline-block rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
                  Connected
                </span>
              )}
            </div>
          </div>
        </label>
      </div>

      {/* Provider and Model Selection - shown for both modes */}
      <div className="mt-8 space-y-6">
        {/* Provider Selection */}
        <div>
          <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-3">
            Select Provider
          </label>
          <div className="grid grid-cols-2 gap-3">
            {providers.map((provider) => (
              <button
                key={provider.id}
                onClick={() => onProviderChange(provider.id)}
                className={`
                  flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 font-medium transition-all
                  ${config.provider === provider.id
                    ? 'border-teal-500 bg-teal-50 text-teal-700 dark:border-teal-400 dark:bg-teal-950/30 dark:text-teal-300'
                    : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300 dark:hover:border-stone-600'
                  }
                `}
              >
                {provider.name}
              </button>
            ))}
          </div>
        </div>

        {/* Model Selection - shown when provider is selected */}
        {config.provider && selectedProvider && (
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
              Select Model
            </label>
            <div className="relative">
              <select
                value={config.selectedModel || ''}
                onChange={(e) => onModelChange(e.target.value)}
                className="
                  w-full appearance-none rounded-lg border border-stone-300 bg-white px-4 py-2.5 pr-10
                  text-stone-900
                  focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20
                  dark:border-stone-600 dark:bg-stone-800 dark:text-stone-100
                  dark:focus:border-teal-400
                "
              >
                {selectedProvider.models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400" />
            </div>

            {/* Model Description */}
            {config.selectedModel && (
              <p className="mt-2 text-sm text-stone-500 dark:text-stone-400">
                {selectedProvider.models.find(m => m.id === config.selectedModel)?.description}
              </p>
            )}
          </div>
        )}

        {/* API Key Input - only for own-key mode */}
        {config.mode === 'own-key' && config.provider && (
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
              API Key
            </label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={localApiKey}
                  onChange={(e) => setLocalApiKey(e.target.value)}
                  placeholder={`Enter your ${selectedProvider?.name} API key`}
                  className="
                    w-full rounded-lg border border-stone-300 bg-white px-4 py-2.5 pr-10
                    text-stone-900 placeholder-stone-400
                    focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20
                    dark:border-stone-600 dark:bg-stone-800 dark:text-stone-100 dark:placeholder-stone-500
                    dark:focus:border-teal-400
                  "
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
                >
                  {showApiKey ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <button
                onClick={handleApiKeySubmit}
                disabled={!localApiKey || isValidating}
                className="
                  flex items-center gap-2 rounded-lg bg-teal-600 px-5 py-2.5 font-medium text-white
                  transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50
                  dark:bg-teal-500 dark:hover:bg-teal-600
                "
              >
                {isValidating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>

            {/* Validation Status */}
            {validationState.status !== 'idle' && validationState.message && (
              <div className={`
                mt-3 flex items-center gap-2 text-sm
                ${validationState.status === 'valid' ? 'text-green-600 dark:text-green-400' : ''}
                ${validationState.status === 'invalid' || validationState.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}
                ${validationState.status === 'validating' ? 'text-stone-500 dark:text-stone-400' : ''}
              `}>
                {validationState.status === 'valid' && <CheckCircle className="h-4 w-4" />}
                {(validationState.status === 'invalid' || validationState.status === 'error') && <XCircle className="h-4 w-4" />}
                {validationState.status === 'validating' && <Loader2 className="h-4 w-4 animate-spin" />}
                {validationState.message}
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  )
}
